import '../Analytics/Analytics.scss';
import './OnboardingFunnel.scss';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';

// Per-person drill-down behind the Onboarding Funnel cards and step rows.
// Answers "who were these people and where exactly did they stop", which the
// aggregate counts on the funnel page cannot.

// Always shown in ET (ZONA's market timezone) regardless of the viewer's
// own browser timezone, with an explicit "ET" label so it's never ambiguous.
const fmtWhen = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })} ET`;
};

const fmtTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit',
  })} ET`;
};

const place = (p) => [p.city, p.region, p.country].filter(Boolean).join(', ') || '-';

// What the page is showing, in words, from the query it was opened with.
const describe = (data) => {
  if (!data) return '';
  if (data.bucket === 'signup_completed') return 'Everyone who completed signup credentials';
  if (data.bucket === 'photo_added') return 'Everyone who added a photo and cleared the mandatory gate';
  if (data.bucket === 'photo_dropoff') return 'Saw the photo screen and left without adding one';
  if (data.step && data.dropped_only) {
    return `Reached ${data.step_label} and never reached ${data.next_step_label || 'the next step'}`;
  }
  if (data.step) return `Everyone who reached ${data.step_label}`;
  return 'Everyone who touched onboarding in this window';
};

const OnboardingFunnelLog = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const days = Number(searchParams.get('days')) || 30;
  const step = searchParams.get('step') || '';
  const dropped = searchParams.get('dropped') || '';
  const bucket = searchParams.get('bucket') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (step) params.set('step', step);
      if (dropped) params.set('dropped', '1');
      if (bucket) params.set('bucket', bucket);
      const { data } = await api.get(`/analytics/onboarding-funnel/log?${params.toString()}`);
      setData(data);
    } catch (err) {
      console.error('Error fetching onboarding funnel log:', err);
      setError(
        err?.response?.status === 503
          ? 'PostHog is not configured on the server yet.'
          : 'Could not load the onboarding log.',
      );
    } finally {
      setLoading(false);
    }
  }, [days, step, dropped, bucket]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  const people = data?.people || [];
  const anonCount = people.filter((p) => p.identity === 'anonymous').length;

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="analytics">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate(`/onboarding-funnel?days=${days}`)}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', cursor: 'pointer', fontSize: 14 }}>
                Back to funnel
              </button>
              <h1 className="analytics__title" style={{ margin: 0 }}>Onboarding Log</h1>
            </div>

            {loading ? (
              <div className="analytics__loading">Loading...</div>
            ) : error ? (
              <div className="analytics__loading" style={{ color: '#ff6b6b' }}>{error}</div>
            ) : (
              <>
                <p className="onboarding__logsub">
                  {describe(data)} &middot; last {days} days &middot; {data.total} {data.total === 1 ? 'person' : 'people'}
                  {data.returned < data.total ? `, showing ${data.returned}` : ''}
                </p>

                {anonCount > 0 && (
                  <div className="onboarding__note">
                    {anonCount} of these never created an account. The name and phone number typed
                    during signup are not saved until the account exists, so for those rows the
                    device, app version and location are the only identifying signals there are.
                  </div>
                )}

                {people.length === 0 ? (
                  <div className="onboarding__pending">Nobody matched this in the window.</div>
                ) : (
                  <div className="analytics__table">
                    <table>
                      <thead>
                        <tr>
                          <th>Last seen</th>
                          <th>Who</th>
                          <th>Location</th>
                          <th>Version</th>
                          <th>Got as far as</th>
                          <th>Outcome</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {people.map((p) => {
                          const isOpen = expanded === p.person_id;
                          return [
                            <tr key={p.person_id}>
                              <td>{fmtWhen(p.last_seen)}</td>
                              <td>
                                {p.user ? (
                                  <div className="onboarding__who">
                                    <span className="onboarding__who-name">
                                      {`${p.user.firstName} ${p.user.lastName}`.trim() || '(no name)'}
                                    </span>
                                    <span className="onboarding__who-sub">{p.user.email || '-'}</span>
                                    {p.user.phone ? (
                                      <span className="onboarding__who-sub">{p.user.phone}</span>
                                    ) : null}
                                  </div>
                                ) : (
                                  <div className="onboarding__who">
                                    <span className="onboarding__who-anon">Never registered</span>
                                    <span className="onboarding__who-sub">{p.device_type || 'unknown device'}</span>
                                  </div>
                                )}
                              </td>
                              <td>{place(p)}</td>
                              <td>{p.app_version || '-'}</td>
                              <td>
                                {p.last_step_label ? (
                                  <span className="onboarding__step-label">{p.last_step_label}</span>
                                ) : (
                                  <span className="onboarding__muted">
                                    no step data{p.app_version && !p.app_version.startsWith('2.0.32') ? ` (${p.app_version} predates step tracking)` : ''}
                                  </span>
                                )}
                              </td>
                              <td>
                                {p.signup_completed || p.onboarding_completed ? (
                                  <span className="onboarding__ok">
                                    signed up{p.photo_added ? ' + photo' : ', no photo'}
                                  </span>
                                ) : (
                                  <div className="onboarding__who">
                                    <span className="onboarding__drop">did not finish</span>
                                    {p.blocked_label ? (
                                      <span className="onboarding__who-sub">
                                        {p.blocked_label}
                                        {p.phone_country ? ` (${p.phone_country})` : ''}
                                      </span>
                                    ) : null}
                                  </div>
                                )}
                              </td>
                              <td>
                                <button
                                  className="onboarding__linkbtn"
                                  onClick={() => setExpanded(isOpen ? null : p.person_id)}>
                                  {isOpen ? 'Hide' : 'Timeline'}
                                </button>
                              </td>
                            </tr>,
                            isOpen ? (
                              <tr key={`${p.person_id}-timeline`}>
                                <td colSpan={7}>
                                  <div className="onboarding__timeline">
                                    {p.timeline.map((t, i) => (
                                      <div className="onboarding__timeline-row" key={`${t.timestamp}-${i}`}>
                                        <span className="onboarding__timeline-time">{fmtTime(t.timestamp)}</span>
                                        <span className="onboarding__timeline-event">
                                          {t.label || t.event}
                                          {t.method ? ` (${t.method === 'social' ? 'Apple or Google sign-in' : t.method})` : ''}
                                          {t.source ? ` (${t.source})` : ''}
                                          {t.reason_label ? ` — ${t.reason_label}` : ''}
                                        </span>
                                      </div>
                                    ))}
                                    {p.user ? (
                                      <div className="onboarding__timeline-foot">
                                        Account created {fmtWhen(p.user.created_at)}
                                        {p.user.onboarded ? ', onboarded' : ', NOT onboarded'}
                                        {p.user.deleted ? ', deleted' : ''}
                                      </div>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            ) : null,
                          ];
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </section>
        </article>
      </div>
    </main>
  );
};

export default OnboardingFunnelLog;
